import type { ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { Link } from '@src/components/Link/Link';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import { InfoIcon } from '@src/components/icons/InfoIcon';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { WarningIcon } from '@src/components/icons/WarningIcon';
import { resolveApiErrorKey } from '@src/i18n/apiError';
import './Hint.css';

export interface HintProps {
  children: ReactNode;
  tone?: 'error' | 'success' | 'info' | 'warning';
  id?: string;
  testId?: string;
}

/** Painted form notice. Screens that already have copy pass children. */
export function Hint({ children, id, testId, tone = 'error' }: HintProps) {
  return (
    <div
      className={`hint${tone !== 'error' ? ` hint--${tone}` : ''}`}
      role={tone === 'error' ? 'alert' : 'status'}
      id={id}
      {...testIdAttr(testId)}
    >
      <span className="hint__icon" aria-hidden="true">
        {tone === 'success' ? <CheckIcon /> : tone === 'info' ? <InfoIcon /> : <WarningIcon />}
      </span>
      <Typography as="div" role="body/sm" color="inherit" className="hint__copy">
        {children}
      </Typography>
    </div>
  );
}

const ERROR_LINKS = {
  register: <Link href="/register" variant="inline" />,
  recover: <Link href="/recover" variant="inline" />,
  login: <Link href="/login" variant="inline" />,
  br: <br />,
  b: <Typography as="span" role="body/strong" color="inherit" />,
  example: <Typography as="span" role="meta/mono" />,
};

export interface HintErrorProps {
  error: string | null;
  testId?: string;
}

/**
 * v1 HintError: the API code is looked up in `errors.*` (with links where the
 * old catalog had them) and shown in the painted box. Unknown codes fall back
 * to errors.UNKNOWN instead of dumping the raw enum.
 */
export function HintError({ error, testId }: HintErrorProps) {
  if (!error) {
    return null;
  }
  return (
    <Hint testId={testId}>
      <Trans i18nKey={resolveApiErrorKey(error)} components={ERROR_LINKS} />
    </Hint>
  );
}
